/**
 * LegacyIntakeRedirect — a single component that stands in for every
 * deprecated Founder-facing intake route.
 *
 * Constitutional purpose (post-corrective-programme, 2026-07-25):
 *   The Founder ratified ONE authoritative Founder evidence-intake path:
 *   /import-todays-recordings (with /talk as the conversational entry).
 *   Every other historical intake surface (`/upload`, `/learner/upload`,
 *   `/instructor/upload`, `/pdi/upload`, `/recording-sessions*`) is now a
 *   dead end that Redirects here.
 *
 *   Blueprint UI R3 §1A · §1B / U-11 · every user-visible string must
 *   come through Universal Intent's single voice.
 *
 *   We do NOT hard-Navigate silently — the Founder direction explicitly
 *   said "any legacy URL should redirect gracefully and explain that
 *   the feature has moved before taking the Founder to the correct
 *   place." So we show a one-sentence narrated notice for a beat, then
 *   navigate.
 */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const CANONICAL_TARGET = "/import-todays-recordings";
const DWELL_MS = 1400;

export default function LegacyIntakeRedirect({ from }) {
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => {
      navigate(CANONICAL_TARGET, { replace: true });
    }, DWELL_MS);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <main
      data-testid="legacy-intake-redirect"
      style={{
        minHeight: "100vh",
        background: "#0f1418",
        color: "#f5f1e4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <section style={{ maxWidth: 520, textAlign: "left" }}>
        <div
          role="status"
          aria-live="polite"
          data-testid="legacy-intake-redirect-narration"
          style={{
            background: "#171d22",
            borderLeft: "3px solid #d9b169",
            padding: "20px 24px",
            borderRadius: 4,
            fontSize: 17,
            lineHeight: 1.5,
          }}
        >
          That’s moved — everything you file with me now lives in one place.
          I’ll take you there.
        </div>
        <p style={{ marginTop: 12, color: "#8a938c", fontSize: 13 }}>
          Redirecting to <code>Import Today’s Recordings</code>…
          {from ? <span> (from <code>{from}</code>)</span> : null}
        </p>
      </section>
    </main>
  );
}
